import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import en from './locales/en.json';
import hi from './locales/hi.json';
import mr from './locales/mr.json';

export type AppLanguage = 'en' | 'hi' | 'mr';
export type TranslationParams = Record<string, string | number>;

export interface LanguageOption {
  code: AppLanguage;
  labelKey: string;
  nativeLabel: string;
}

export const LANGUAGE_OPTIONS: LanguageOption[] = [
  { code: 'en', labelKey: 'language.english', nativeLabel: 'English' },
  { code: 'hi', labelKey: 'language.hindi', nativeLabel: 'हिंदी' },
  { code: 'mr', labelKey: 'language.marathi', nativeLabel: 'मराठी' },
];

export const LANGUAGE_STORAGE_KEY = 'hrms.preferredLanguage';

type LocaleResource = Record<string, unknown>;

const flattenResource = (
  resource: LocaleResource,
  prefix = '',
  output: Record<string, string> = {}
) => {
  Object.entries(resource).forEach(([key, value]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (typeof value === 'string') {
      output[path] = value;
    } else if (value && typeof value === 'object' && !Array.isArray(value)) {
      flattenResource(value as LocaleResource, path, output);
    }
  });
  return output;
};

export const flattenedResources: Record<AppLanguage, Record<string, string>> = {
  en: flattenResource(en),
  hi: flattenResource(hi),
  mr: flattenResource(mr),
};

const englishValueToKey = new Map<string, string>();
Object.entries(flattenedResources.en).forEach(([key, value]) => {
  if (!englishValueToKey.has(value)) {
    englishValueToKey.set(value, key);
  }
});

const normalizeLanguage = (value: string | null | undefined): AppLanguage => {
  if (value === 'hi' || value === 'mr') return value;
  return 'en';
};

export const normalizeExternalLanguage = (value: string | null | undefined): AppLanguage | null => {
  if (!value) return null;

  let normalized = value.trim().toLowerCase();
  if (!normalized) return null;

  try {
    normalized = decodeURIComponent(normalized);
  } catch {
    // Keep the original value if the external tool gives a malformed URI value.
  }

  const parts = normalized.split('/').filter(Boolean);
  const targetLanguage = parts.length > 0 ? parts[parts.length - 1] : normalized;
  const baseLanguage = targetLanguage.split(/[-_]/)[0];

  if (baseLanguage === 'en' || baseLanguage === 'hi' || baseLanguage === 'mr') {
    return baseLanguage;
  }

  return null;
};

const interpolate = (value: string, params?: TranslationParams) => {
  if (!params) return value;
  return Object.entries(params).reduce(
    (text, [key, paramValue]) => text.replaceAll(`{${key}}`, String(paramValue)),
    value
  );
};

const resolveTranslationKey = (keyOrEnglishText: string) => {
  if (flattenedResources.en[keyOrEnglishText] !== undefined) {
    return keyOrEnglishText;
  }
  return englishValueToKey.get(keyOrEnglishText);
};

export const translateAppText = (
  language: AppLanguage,
  keyOrEnglishText: string,
  params?: TranslationParams
) => {
  const translationKey = resolveTranslationKey(keyOrEnglishText);
  if (!translationKey) {
    return interpolate(keyOrEnglishText, params);
  }

  const value =
    flattenedResources[language][translationKey] ??
    flattenedResources.en[translationKey] ??
    keyOrEnglishText;
  return interpolate(value, params);
};

const readStoredLanguage = (): AppLanguage => {
  try {
    return normalizeLanguage(window.localStorage.getItem(LANGUAGE_STORAGE_KEY));
  } catch {
    return 'en';
  }
};

const writeStoredLanguage = (language: AppLanguage) => {
  try {
    window.localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
  } catch {
    // Storage can be unavailable in private or restricted browser contexts.
  }
};

const readGoogleTranslateCookieValue = () => {
  if (typeof document === 'undefined') return null;

  const cookie = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith('googtrans='));

  return cookie ? cookie.slice('googtrans='.length) : null;
};

const readDocumentElementLanguage = () => {
  if (typeof document === 'undefined' || !document.documentElement) return null;
  return normalizeExternalLanguage(document.documentElement.getAttribute('lang'));
};

interface LanguageContextValue {
  language: AppLanguage;
  setLanguage: (language: AppLanguage) => void;
  t: (key: string, params?: TranslationParams) => string;
  getLanguageName: (language: AppLanguage) => string;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [language, setLanguageState] = useState<AppLanguage>(readStoredLanguage);
  const languageRef = useRef(language);
  const appAppliedDocumentLanguageRef = useRef<AppLanguage | null>(null);
  const googleTranslateCookieRef = useRef<string | null>(null);

  useEffect(() => {
    languageRef.current = language;
    appAppliedDocumentLanguageRef.current = language;
    document.documentElement.lang = language;
    document.documentElement.dir = 'ltr';
    document.documentElement.dataset.language = language;
  }, [language]);

  useEffect(() => {
    const handleStoredLanguageChange = (event: StorageEvent) => {
      if (event.key === LANGUAGE_STORAGE_KEY) {
        setLanguageState(normalizeLanguage(event.newValue));
      }
    };

    window.addEventListener('storage', handleStoredLanguageChange);
    return () => window.removeEventListener('storage', handleStoredLanguageChange);
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof document === 'undefined' || !document.documentElement) {
      return undefined;
    }

    googleTranslateCookieRef.current = readGoogleTranslateCookieValue();

    const applyExternalLanguage = (externalLanguage: AppLanguage | null) => {
      if (!externalLanguage || externalLanguage === languageRef.current) return;

      languageRef.current = externalLanguage;
      setLanguageState(externalLanguage);
      writeStoredLanguage(externalLanguage);
    };

    const syncFromDocumentLanguage = () => {
      const documentLanguage = readDocumentElementLanguage();
      if (!documentLanguage) return;
      if (documentLanguage === appAppliedDocumentLanguageRef.current) return;

      applyExternalLanguage(documentLanguage);
    };

    const syncFromGoogleTranslateCookie = () => {
      const nextCookieValue = readGoogleTranslateCookieValue();
      if (nextCookieValue === googleTranslateCookieRef.current) return;

      googleTranslateCookieRef.current = nextCookieValue;
      applyExternalLanguage(normalizeExternalLanguage(nextCookieValue));
    };

    const syncExternalLanguage = () => {
      syncFromGoogleTranslateCookie();
      syncFromDocumentLanguage();
    };

    const observer =
      typeof MutationObserver !== 'undefined'
        ? new MutationObserver(syncExternalLanguage)
        : null;

    observer?.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['lang', 'class'],
    });

    const intervalId = window.setInterval(syncFromGoogleTranslateCookie, 1000);
    window.addEventListener('focus', syncExternalLanguage);
    window.addEventListener('pageshow', syncExternalLanguage);

    return () => {
      observer?.disconnect();
      window.clearInterval(intervalId);
      window.removeEventListener('focus', syncExternalLanguage);
      window.removeEventListener('pageshow', syncExternalLanguage);
    };
  }, []);

  const setLanguage = useCallback((nextLanguage: AppLanguage) => {
    const normalized = normalizeLanguage(nextLanguage);
    languageRef.current = normalized;
    setLanguageState(normalized);
    writeStoredLanguage(normalized);
  }, []);

  const t = useCallback(
    (key: string, params?: TranslationParams) => translateAppText(language, key, params),
    [language]
  );

  const getLanguageName = useCallback(
    (code: AppLanguage) => {
      const option = LANGUAGE_OPTIONS.find((item) => item.code === code);
      return option ? t(option.labelKey) : t('language.english');
    },
    [t]
  );

  const value = useMemo(
    () => ({ language, setLanguage, t, getLanguageName }),
    [getLanguageName, language, setLanguage, t]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};
